import {
  SB_ID,
  PF_ID,
  usePriceFreezeSession,
} from '@/hooks/usePriceFreezeSession';
import { useToggle } from '@/hooks/useToggle';
import {
  useContactDetailsToShoppingBasketMutation,
  useCreatePriceFreeze,
  useCreateShoppingBasketMutation,
} from '@/hooks/GQL';
import {
  useIsAvailableOffer,
  useOffer,
  useSearchCriteriaSelector,
} from '@/providers/PriceFreezeOfferProvider';
import { useShowNotAvailable } from '@/providers/NotAvailableProvider';
import { selectPaxType } from '@/selectors';
import { useRepricingInfo } from '@/providers/RepricingInfoProvider';
import { createPriceFreezeRequest } from '@/components/PaxPage/createPriceFreezeRequest';
import { getCountryIsoCode } from '@/utils/countryCode';
import { useCountryList } from '@/hooks/useCountryList';

export const useHomePageAction = ({ onContinue, onError = null }) => {
  const [, setSessionItem] = usePriceFreezeSession();
  const [error, toggleError] = useToggle(false);
  const available = useIsAvailableOffer();
  const { showPopup } = useShowNotAvailable();

  const { loading, mutate: createShoppingBasket } =
    useCreateShoppingBasketMutation({
      onSuccess({ data }) {
        setSessionItem(SB_ID, data.createShoppingBasket.shoppingBasketId);
        setSessionItem(PF_ID, null);
        onContinue(data.createShoppingBasket.shoppingBasketId);
      },
      onError: () => {
        if (onError) onError();
        toggleError();
      },
    });

  const _onContinue = () => {
    if (!available) {
      showPopup();
    } else {
      createShoppingBasket();
    }
  };

  return {
    loading,
    error,
    toggleError,
    onContinue: _onContinue,
    available,
  };
};

export function useAddContactDetailsAction({ onAddSuccess }) {
  const [error, toggleError] = useToggle();
  const [getSessionItem] = usePriceFreezeSession();

  const { loading, mutate } = useContactDetailsToShoppingBasketMutation({
    onSuccess() {
      onAddSuccess();
    },
    onError() {
      toggleError();
    },
  });
  const {
    countries,
    loading: countriesLoading,
    error: countriesError,
  } = useCountryList();

  const addContactDetails = (formData, shoppingBasketId) => {
    if (!formData) return;
    const countryCode = getCountryIsoCode(formData, countries);
    mutate({
      request: {
        shoppingId: shoppingBasketId || getSessionItem(SB_ID),
        name: formData.name,
        lastName: formData.surname,
        email: formData.email,
        address: 'PRICE_FREEZE_PLACEHOLDER',
        cityName: 'PRICE_FREEZE_PLACEHOLDER',
        countryPhoneNumber: formData.country_code.slice(1),
        countryCode,
        phoneNumber: formData.phone,
      },
    });
  };

  return {
    error: countriesError || error,
    loading: countriesLoading || loading,
    addContactDetails: countries ? addContactDetails : () => {},
  };
}

export function useCreateProductAction({ onCreateSuccess, onError = null }) {
  const [error, toggleError] = useToggle();
  const offer = useOffer();
  const paxType = useSearchCriteriaSelector(selectPaxType);
  const [getSessionItem, setSessionItem] = usePriceFreezeSession();

  const { repricing } = useRepricingInfo() || {};

  const { loading, mutate } = useCreatePriceFreeze({
    onSuccess({ data }) {
      setSessionItem(PF_ID, data.itineraryPriceFreeze);
      onCreateSuccess();
    },
    onError() {
      if (onError) onError();
      toggleError();
    },
  });
  const {
    countries,
    loading: countriesLoading,
    error: countriesError,
  } = useCountryList();
  const createProduct = (formData, shoppingBasketId) => {
    const request = createPriceFreezeRequest({
      formData,
      offer,
      paxType,
      shoppingBasketId: shoppingBasketId || getSessionItem(SB_ID),
      repricing,
      countries,
    });
    mutate({ request });
  };

  return {
    error: countriesError || error,
    loading: countriesLoading || loading,
    createProduct: countries ? createProduct : () => {},
  };
}
